import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { DataContext } from '../contexts/DataContext';
import InfoCard from '../components/InfoCard/InfoCard';
import RecyclingInfo from '../components/RecyclingInfo/RecyclingInfo';
import MaintenanceSummary from '../components/MaintenanceSummary/MaintenanceSummary';
import { formatDate, formatDateTime } from '../utils/dateUtils';
import { countValidRepairs, countValidCleaning } from '../utils/maintenanceUtils';
import { getSafeText } from '../utils/valueResolver';

function MaintenancePage() {
  const { t, i18n } = useTranslation();
  const { data } = useContext(DataContext);
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get('qr') || '';
  const lang = searchParams.get('lang') || i18n.language || 'ja';
  const search = `?qr=${qr}&lang=${lang}`;

  const validCleanings = data.MaintenanceInfo?.cleaning?.filter(item =>
    item?.date && typeof item.date === 'string'
  );

  const validRepairs = data.MaintenanceInfo?.repair?.filter(item =>
    item && typeof item === 'object' &&
    Object.values(item).some(value =>
      Array.isArray(value) ? value.length > 0 : !!value
    )
  );

  const cleaningItems = validCleanings?.length > 0
    ? [
        { label: t('maintenance.date'), value: formatDate(validCleanings[0].date) },
        { label: t('maintenance.cleaningType'), value: getSafeText(validCleanings[0].type, lang)},
        { label: t('maintenance.detergent'), value: getSafeText(validCleanings[0].detergent, lang)},
        { label: t('maintenance.company'), value: getSafeText(validCleanings[0].company, lang)}
      ]
    : [];

  const repairItems = validRepairs?.length > 0
    ? [
        { label: t('maintenance.date'), value: formatDate(validRepairs[0].date) },
        { label: t('maintenance.repairPart'), value: getSafeText(validRepairs[0].part, lang) },
        { label: t('maintenance.repairContent'), value: getSafeText(validRepairs[0].content, lang) },
        { label: t('maintenance.company'), value: getSafeText(validRepairs[0].company, lang)}
      ]
    : [];

  return (
    <>
      <div className="box-left">
        <InfoCard
          iconSrc="/images/icon/icon_cleaning.svg"
          label={t('maintenance.cleaning')}
          date={cleaningItems[0]?.value || '-'}
          count={countValidCleaning(data.MaintenanceInfo?.cleaning)}
        />
        <InfoCard
          iconSrc="/images/icon/icon_repair.svg"
          label={t('maintenance.repair')}
          date={repairItems[0]?.value || '-'}
          count={countValidRepairs(data.MaintenanceInfo?.repair)}
        />
        <p className="updated-at">{formatDateTime(data.updated)} Updated</p>

        <RecyclingInfo isMobile={false} />
        <RecyclingInfo isMobile={true} />
      </div>

      <div className="box-right">
        <MaintenanceSummary
          icon="icon_cleaning"
          title={t('maintenance.cleaning')}
          items={cleaningItems}
          historyLink={{
            pathname: "/maintenance/history/cleaning",
            search,
            state: { data: data.MaintenanceInfo.cleaning }
          }}
          lang={lang}
        />
        <MaintenanceSummary
          icon="icon_repair"
          title={t('maintenance.repair')}
          items={repairItems}
          historyLink={{
            pathname: "/maintenance/history/repair",
            search,
            state: { data: data.MaintenanceInfo.repair }
          }}
          lang={lang}
        />
      </div>
    </>
  );
}

export default MaintenancePage;
